// RecordEditAttachmentList - MODULE
// ***********************************************************************************************************************
import { Alert } from "@mui/material";
import downloadjs from "downloadjs";
import * as React from "react";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as attachmentService from "../../../models/services/attachmentService";
import * as recordService from "../../../models/services/recordService";
import { AttachmentList } from "../../attachments/AttachmentList";

// RecordEditAttachmentList
export interface IRecordEditAttachmentListProp {
    isDisabled?: boolean;
    isDisabledMessage?: string;
    isReadOnly?: boolean;
    companyId: string;
    recordId: string;
    subOwnerId?: string;
    userCode: string;
    category: number;
    title?: string;
    margin?: string | number;
    attachments: IFileAttachment[];
    size: "sm" | "md";
    onRemoved: (ids: string[]) => void;
    onAdded: (attachments: IFileAttachment[]) => void;
}

export class RecordEditAttachmentList extends React.Component<IRecordEditAttachmentListProp, {}> {
    static defaultProps = {
        size: "sm",
        margin: "1.5rem 0 0 0",
    };

    handleDownload = async (id: string) => {
        if (!id) return;
        const props = this.props;
        const attachment = props.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    getRemoveSaveData = (props: IRecordEditAttachmentListProp, attachmentIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("ids", attachmentIds);
        return result;
    };

    handleRemove = async (id: string) => {
        if (!id) return;
        const props = this.props;
        const saveData = this.getRemoveSaveData(this.props, [id]);
        const result = AppUtils.callService(() => recordService.recordEditRemoveAttachments(saveData.obj));
        if (!result) return;
        props.onRemoved([id]);
    };

    getAddSaveData = async (props: IRecordEditAttachmentListProp, attachments: IFileAttachment[]): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("subOwnerId", props.subOwnerId ?? "");
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("category", props.category.toString(10));
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(attachments.filter(i => !!i.file)));
        return result;
    };
    
    handleAdd = async (fileList: FileList) => {
        const props = this.props;
        const attachments = await FileAttachment.addFileListToFileAttachments([], fileList, props.category, props.subOwnerId);
        if (attachments.length < 1) return;
        const saveData = await this.getAddSaveData(this.props, attachments);
        const result = await AppUtils.callService(() => recordService.recordEditAddAttachments(saveData.obj));
        if (!result) return;
        EditItem.setItemIdsFromIds(attachments, result.ids);
        props.onAdded(attachments);
    };

    render() {
        const props = this.props;
        return (
            <>
                {!!props.isDisabled &&
                    <Alert severity="info" sx={{ marginTop: "1rem" }}>{props.isDisabledMessage}</Alert>
                }
                {!props.isDisabled &&
                    <AttachmentList
                        margin={props.margin}
                        isReadOnly={props.isReadOnly}
                        title={props.title}
                        attachments={props.attachments}
                        size={props.size}
                        onRemoveAttachment={this.handleRemove}
                        onDownloadAttachment={this.handleDownload}
                        onAddAttachments={this.handleAdd}
                    />
                }
            </>
        );
    }
}